<template>
  <Modal name="CREATE_UPDATE_SERVICE_PROVIDER_MODAL" :title="title" actions>
    <template v-slot:body>
      <h-loading v-if="isLoadingBoard" />
      <v-form v-show="!isLoadingBoard" v-model="valid" ref="serviceProviderForm" lazy-validation>
        <v-text-field
          v-model="name"
          :label="$t('forms.completeName')"
          :rules=[rules.name]
          autocomplete="name"
          outlined
        />

        <HDocumentID
          v-model="document"
          :country="country"
          :label="$t('forms.document')"
          :rules="[rules.documentCheck]"
          :default-value="serviceProviderDocument"
        />

        <div v-if="!hasServiceProviderId" class="mt-4">
          <v-divider class="mb-4" />
          <div class="subtitle-1 font-weight-medium mb-2">
            {{ $t('create.actionMenu.newUser') }}
          </div>

          <v-text-field
            v-model="hostName"
            :label="$t('forms.completeName')"
            :rules=[rules.hostName]
            outlined
          />

          <HDocumentID
            v-model="hostCpf"
            :country="country"
            :label="$t('forms.cpf')"
            :rules=[rules.cpf]
          />

          <v-text-field
            v-model="hostRg"
            :label="$t('host.label.rg')"
            :rules=[rules.rg]
            outlined
          />

          <v-text-field
            v-model="hostUsername"
            :label="$t('forms.email')"
            :rules=[rules.email]
            autocomplete="email"
            outlined
          />

          <v-text-field
            v-model="hostPhone"
            :label="$t('forms.phone')"
            :rules=[rules.phone]
            autocomplete="tel"
            outlined
          />

          <v-text-field
            v-model="hostPassword"
            :label="$t('onboarding.register.password')"
            :rules=[rules.password]
            :type="showHostPassword ? 'text' : 'password'"
            autocomplete="new-password"
            outlined
            :append-icon="showHostPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="showHostPassword = !showHostPassword"
          />

          <HCitySelect
            :country="country"
            :multiple="false"
            @input="onCitySelected"
          />

          <v-text-field
            v-model="hostBirthDate"
            :label="$t('dashboard.cardAppointment.birthDate')"
            :rules=[rules.birthDate]
            placeholder="DD/MM/AAAA"
            outlined
            inputmode="numeric"
            @input="onBirthDateInput"
          />
        </div>
      </v-form>
    </template>
    <template v-slot:actions>
      <div class="d-flex">
        <v-btn @click="closeAllModals" :disabled="isLoading" class="grey--text mr-5" elevation="0" large text>
          {{ $t('forms.cancel') }}
        </v-btn>
        <v-btn @click="save" :loading="isLoading" color="primary" elevation="0" large>
          {{ $t('forms.save') }}
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { name as nameRule, documentCheck } from '@/util/errorHandling'
import { emailRule, phoneRule, passwordRule, rgRule } from '@/util/defaultValidationRules'
import { unmaskText } from '../../../util/helpers'
import { validateCPF, normalizeBirthDateToIso, formatBirthDateToBRMask } from '@/util/validate'

import Modal from '../../../components/app/Modal.vue'
import HDocumentID from '../../../components/app/HDocumentID'
import HCitySelect from '../../../components/app/HCitySelect.vue'

export default {
  name: 'ServiceProviderCreateUpdate',

  components: {
    Modal,
    HDocumentID,
    HCitySelect,
  },

  computed: {
    ...mapGetters(['serviceProvider', 'isLoadingBoard']),

    hasServiceProviderId() {
      return !!(this.serviceProvider && this.serviceProvider.id)
    },

    serviceProviderDocument() {
      return this.serviceProvider ? this.serviceProvider.document : null
    },

    title() {
      return this.hasServiceProviderId
        ? this.$t('serviceProviders.action.edit')
        : this.$t('serviceProviders.action.createVendors')
    },

    country() {
      return this.user?.country ?? localStorage.getItem('user_country')
    },
  },

  methods: {
    ...mapActions(['createServiceProvider', 'updateServiceProvider']),
    ...mapMutations(['closeAllModals']),

    onCitySelected(value) {
      const id = value && typeof value === 'object' ? value.id : value
      this.hostCityId = id ? Number(id) : null
    },

    onBirthDateInput(value) {
      this.hostBirthDate = formatBirthDateToBRMask(value)
    },

    resetFields() {
      this.valid = false
      this.name = ''
      this.document = ''
      this.hostName = ''
      this.hostCpf = ''
      this.hostRg = ''
      this.hostUsername = ''
      this.hostPassword = ''
      this.hostPhone = ''
      this.hostCityId = null
      this.hostBirthDate = ''
      this.showHostPassword = false
    },

    fillFields() {
      if (this.hasServiceProviderId) {
        this.name = this.serviceProvider.name
        this.document = this.serviceProvider.document
        return
      }
      this.resetFields()
    },

    save() {
      if (!this.$refs.serviceProviderForm.validate()) return

      const payload = {
        id: this.serviceProvider.id ?? null,
        name: this.name,
        document: unmaskText(this.document),
      }

      this.isLoading = true

      if (payload.id) {
        this.updateServiceProvider(payload)
          .then(() => this.resetFields())
          .finally(() => { this.isLoading = false })
        return
      }

      payload.host_name = this.hostName
      payload.cpf = unmaskText(this.hostCpf)
      payload.rg = this.hostRg || null
      payload.username = this.hostUsername
      payload.password = this.hostPassword
      payload.phone = this.hostPhone
      payload.city_id = Number(this.hostCityId)
      payload.birth_date = normalizeBirthDateToIso(this.hostBirthDate)

      this.createServiceProvider(payload)
        .then(() => this.resetFields())
        .finally(() => { this.isLoading = false })
    },
  },

  watch: {
    serviceProvider: {
      handler() {
        this.fillFields()
      },
      immediate: true,
    },
  },

  data() {
    return {
      isLoading: false,
      valid: false,
      showHostPassword: false,

      name: '',
      document: '',
      hostName: '',
      hostCpf: '',
      hostRg: '',
      hostUsername: '',
      hostPassword: '',
      hostPhone: '',
      hostCityId: null,
      hostBirthDate: '',

      rules: {
        name: nameRule,
        documentCheck,
        hostName: nameRule,
        cpf: (v) => {
          if (this.country !== 'BR') return true
          return validateCPF(unmaskText(v || '')) || this.$t('forms.invalidCpf')
        },

        rg: rgRule,
        email: emailRule,
        phone: phoneRule,
        password: passwordRule,

        birthDate: (v) => !!normalizeBirthDateToIso(v) || this.$t('forms.invalidBirthDate'),
      },
    }
  },
}
</script>
